/**
 * HOOK: useProducts (VERSION REFACTORISÉE)
 * Utilise le hook useCRUD générique
 *
 * 🎯 Avant: 138 lignes | Après: 22 lignes | Réduction: 84%
 */

import { useCallback } from 'react';
import type { Product, CreateProductDto, UpdateProductDto } from '../types/product';
import { useCRUD } from './useCRUD';
import { generateSlug } from '../lib/csv-import';

export function useProducts() {
  const crud = useCRUD<Product, CreateProductDto, UpdateProductDto>('products', {
    select: '*',
    orderBy: { column: 'name', ascending: true },
  });

  /**
   * Création d'un produit avec génération automatique du slug
   */
  const createWithSlug = useCallback(
    async (data: Omit<CreateProductDto, 'slug'>) => {
      const slug = generateSlug(data.name);
      return crud.create({ ...data, slug });
    },
    [crud]
  );

  return {
    // API spécifique (pour compatibilité avec code existant)
    products: crud.data,
    loading: crud.loading,
    error: crud.error,
    refreshProducts: crud.refresh,
    getProductById: crud.getById,
    createProduct: createWithSlug,
    updateProduct: crud.update,
    deleteProduct: crud.remove,
    deleteProducts: crud.removeMany,

    // API générique (pour useEntityList)
    data: crud.data,
    create: createWithSlug,
    update: crud.update,
    remove: crud.remove,
    removeMany: crud.removeMany,
  };
}

/**
 * MIGRATION GUIDE:
 *
 * Ancien code:
 * const { products, loading, error, createProduct, updateProduct, deleteProduct } = useProducts();
 *
 * Nouveau code:
 * const { data: products, loading, error, create: createProduct, update: updateProduct, remove: deleteProduct } = useProducts();
 *
 * ✅ API identique, juste renommer les méthodes
 */
